// Schreiben-Assistent Schritt 4: Anlagen (hochladen oder aus der Akte), Versandweg je Empfänger (Standard aus dem
// Kontakt, Vorlage „immer zusätzlich per Post“), Post-Optionen, Bestätigung, Senden mit Fortschritt und Protokoll.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, CheckCircle, CircleNotch, FilePdf, Folder, Paperclip, PaperPlaneTilt, UploadSimple, WarningCircle, X } from '@phosphor-icons/react';
import { ImmoButton, SchalterZeile } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { speicherName } from '../../../lib/storage/speicherName';
import { ladeOrdnerInhalt, ORDNER_MIME, type DriveEintrag } from '../../../lib/dokumente/aktenschrankApi';
import { EINSCHREIBEN, type BriefOptionen } from '../../../lib/epost/epost';
import { mimeAusName } from '../../../lib/epost/emailZuBrief';
import { moeglich, portalOhneLogin, standardWege, versandSperre } from '../../../lib/schreiben/zustellweg';
import { fuehreAus, standardDienste } from '../../../lib/schreiben/versand';
import { ladeStandardKonto, legeLaufAn } from '../../../lib/schreiben/protokoll';
import { hatDruckstapel } from '../../../lib/schreiben/druckstapel';
import { ladeEigeneEmailSignatur } from '../../../lib/schreiben/signatur';
import { vorbelegeWiedervorlage, wiedervorlageNachVersand, type WiedervorlageWahl } from '../../../lib/schreiben/wiedervorlage';
import { ladeSperrvermerke, mitSperrvermerken } from '../../../lib/schreiben/empfaenger';
import { ZUSTELLWEGE, ZUSTELLWEG_LABEL, type Zustellweg } from '../../../lib/schreiben/typen';
import { FELD, LABEL } from '../emails/bereich/Formular';
import { unvollstaendig } from './SchrittInhalt';
import { useGestaltung } from './useGestaltung';
import { EINGEBAUT, ladePdfGestaltung, standardBriefkopf } from '../../../lib/schreiben/gestaltung';
import { schluessel, werteVon, type SetZustand, type Zustand } from './zustand';

const DRUCKBAR = /\.(pdf|png|jpe?g)$/i;

/** Kleiner Ordner-Browser für die Akte der beteiligten Objekte. */
function AktenAuswahl({ objektIds, onWahl }: { objektIds: string[]; onWahl: (d: DriveEintrag) => void }) {
  const [objekte, setObjekte] = useState<{ id: string; name: string; drive: string }[]>([]);
  const [pfad, setPfad] = useState<{ id: string; name: string }[]>([]);
  const [inhalt, setInhalt] = useState<DriveEintrag[] | null>(null);
  useEffect(() => {
    if (!objektIds.length) return;
    void supabase.from('objekte').select('id, objektnummer, bezeichnung, gdrive_folder_id').in('id', objektIds).then(({ data }) =>
      setObjekte((data ?? []).filter((o) => o.gdrive_folder_id).map((o) => ({ id: o.id, name: [o.objektnummer, o.bezeichnung].filter(Boolean).join(' '), drive: o.gdrive_folder_id as string }))));
  }, [objektIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const oeffne = (id: string, name: string, neu = true) => {
    setInhalt(null);
    setPfad((p) => (neu ? [...p, { id, name }] : p));
    void ladeOrdnerInhalt(id).then(setInhalt, () => setInhalt([]));
  };
  if (!objektIds.length) return <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Kein Empfänger mit Objekt – Anlagen bitte hochladen.</span>;
  if (!pfad.length) return (
    <div className="flex flex-wrap" style={{ gap: 6 }}>
      {objekte.map((o) => <ImmoButton key={o.id} groesse={32} icon={Folder} onClick={() => oeffne(o.drive, o.name)}>{o.name}</ImmoButton>)}
    </div>
  );
  return (
    <div className="flex flex-col" style={{ gap: 4, maxHeight: 260, overflowY: 'auto' }}>
      <button type="button" onClick={() => { const p = pfad.slice(0, -1); setPfad(p); if (p.length) oeffne(p[p.length - 1].id, '', false); }}
        className="flex items-center" style={{ gap: 6, border: 'none', background: 'transparent', color: 'var(--acc)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 4 }}>
        <ArrowUp size={13} weight="bold" />{pfad.map((p) => p.name).filter(Boolean).join(' / ') || 'zurück'}
      </button>
      {inhalt === null && <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Lädt …</span>}
      {inhalt?.map((d) => (
        <button key={d.id} type="button" disabled={d.mimeType !== ORDNER_MIME && !DRUCKBAR.test(d.name)}
          onClick={() => (d.mimeType === ORDNER_MIME ? oeffne(d.id, d.name) : onWahl(d))}
          className="immo-hover-flaeche flex items-center text-left" style={{ gap: 8, minHeight: 36, padding: '0 8px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, cursor: 'pointer', opacity: d.mimeType === ORDNER_MIME || DRUCKBAR.test(d.name) ? 1 : 0.4 }}>
          {d.mimeType === ORDNER_MIME ? <Folder size={15} /> : <FilePdf size={15} />}<span className="truncate">{d.name}</span>
        </button>
      ))}
    </div>
  );
}

export function SchrittVersand({ zustand: z, setZustand }: { zustand: Zustand; setZustand: SetZustand }) {
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [konto, setKonto] = useState<{ id: string; email_adresse: string } | null>(null);
  const gestaltung = useGestaltung();
  const [fehler, setFehler] = useState<string | null>(null);
  const [akte, setAkte] = useState(false);
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [bestaetigung, setBestaetigung] = useState('');
  const [laeuft, setLaeuft] = useState<{ fertig: number; gesamt: number } | null>(null);
  const [ergebnis, setErgebnis] = useState<{ gesendet: number; teilweise: number; fehler: number } | null>(null);
  const datei = useRef<HTMLInputElement>(null);

  // Druck/Schriftform erst nach der Migration Etappe 3 (sonst scheitert das Protokoll an der Prüfregel der Wege)
  const [druckDa, setDruckDa] = useState(false);
  // Begleit-E-Mails enden mit der persönlichen E-Mail-Signatur (Björn: jeder Mitarbeiter seine Schlussformel)
  const [signatur, setSignatur] = useState<{ name: string; html: string } | null>(null);
  useEffect(() => {
    void ladeStandardKonto().then(setKonto, (e: Error) => setFehler(e.message)); void hatDruckstapel().then(setDruckDa);
    void ladeEigeneEmailSignatur().then(setSignatur, () => setSignatur(null));
  }, []);
  const angeboteneWege = ZUSTELLWEGE.filter((w) => w !== 'druck' || druckDa);
  const heuteIso = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  // Etappe 4: Wiedervorlage vorbelegen – Tag nach einer Frist aus den Eingaben, sonst heute + Tage der Vorlage;
  // folgt späteren Eingaben, bis man Schalter oder Datum selbst anfasst
  useEffect(() => {
    setZustand((x) => {
      const neu = vorbelegeWiedervorlage(x.wiedervorlage, heuteIso, x.wiedervorlageTage, x.eingaben);
      return neu === x.wiedervorlage ? x : { ...x, wiedervorlage: neu };
    });
  }, [z.eingaben, z.wiedervorlageTage]); // eslint-disable-line react-hooks/exhaustive-deps
  const setzeWv = (teil: Partial<WiedervorlageWahl>) => setZustand((x) => ({ ...x, wiedervorlage: x.wiedervorlage && { ...x.wiedervorlage, ...teil, manuell: x.wiedervorlage.manuell || 'an' in teil || 'datum' in teil } }));
  const wvOhneDatum = !!z.wiedervorlage?.an && !z.wiedervorlage.datum;
  const [ticket, setTicket] = useState<{ id: string; ticket_nummer: string } | null>(null);
  const [ticketFehler, setTicketFehler] = useState<string | null>(null);
  // Standard-Weg je Empfänger einmal setzen
  useEffect(() => {
    setZustand((x) => {
      const wege = { ...x.wege };
      for (const e of x.empfaenger) if (!(schluessel(e) in wege)) wege[schluessel(e)] = standardWege(e, x.immerPost, x.schriftform).wege;
      return { ...x, wege };
    });
  }, [z.empfaenger.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const offen = useMemo(() => unvollstaendig(z), [z]);
  const liste = z.empfaenger.map((e) => {
    const wege = z.wege[schluessel(e)] ?? [];
    const m = moeglich(e);
    const unmoeglich = wege.filter((w) => !m[w]);
    const sperre = !wege.length ? (e.sperrvermerk ? `Sperrvermerk: ${e.sperrvermerk}` : standardWege(e, false).warnung ?? 'Kein Zustellweg gewählt – wird nicht gesendet')
      : versandSperre(e, wege, z.schriftform) ?? (offen.has(schluessel(e)) ? `Fehlt: ${offen.get(schluessel(e))!.join(', ')}` : null);
    return { e, wege, m, unmoeglich, sperre };
  });
  const versendbar = liste.filter((x) => x.wege.length && !x.sperre);
  const zaehler = Object.fromEntries(ZUSTELLWEGE.map((w) => [w, versendbar.filter((x) => x.wege.includes(w)).length])) as Record<Zustellweg, number>;
  const mitPost = zaehler.post > 0, mitDruck = zaehler.druck > 0;
  // #607: Erstellen und im Portal freigeben in einem Schritt – für alle Empfänger (Unmögliches fällt je Person weg)
  const KOMBIS: { label: string; wege: Zustellweg[]; tip: string }[] = [
    { label: 'E-Mail + Freigabe', wege: ['email', 'portal'], tip: 'Per E-Mail senden und im Portal freigeben' },
    ...(druckDa ? [{ label: 'Drucken + Freigabe', wege: ['druck', 'portal'] as Zustellweg[], tip: 'In den Druckstapel legen und im Portal freigeben' }] : []),
    { label: 'Post + Freigabe', wege: ['post', 'portal'], tip: 'Per Post (E-POST) senden und im Portal freigeben' },
    { label: 'Nur Freigabe', wege: ['portal'], tip: 'Nur in der Objektakte ablegen und im Portal freigeben – ohne Versand' },
  ];
  const setzeFuerAlle = (wunsch: Zustellweg[]) => setZustand((x) => {
    const wege = { ...x.wege };
    for (const e of x.empfaenger) {
      const m = moeglich(e);
      const neu = wunsch.filter((w) => m[w]);
      if (neu.length) wege[schluessel(e)] = neu;
    }
    return { ...x, wege };
  });
  const mitPortal = z.empfaenger.some((e) => moeglich(e).portal);
  /** Schriftform umschalten: Standardwege aller Empfänger neu (bei Schriftform: Druck) */
  const setzeSchriftform = (an: boolean) => setZustand((x) => ({
    ...x, schriftform: an, wege: Object.fromEntries(x.empfaenger.map((e) => [schluessel(e), standardWege(e, x.immerPost, an).wege])),
  }));
  const objektIds = [...new Set(z.empfaenger.map((e) => e.objektId).filter((x): x is string => !!x))];

  const hochladen = async (files: FileList | null) => {
    if (!files?.length || !konto) return;
    setLaedtHoch(true); setFehler(null);
    try {
      for (const f of Array.from(files)) {
        if (!DRUCKBAR.test(f.name)) throw new Error(`${f.name}: nur PDF, PNG oder JPG`);
        if (f.size > 20 * 1024 * 1024) throw new Error(`${f.name}: größer als 20 MB`);
        const path = `${konto.id}/${Date.now()}_${speicherName(f.name)}`;
        const { error } = await supabase.storage.from('email-anhaenge').upload(path, f, { contentType: f.type || mimeAusName(f.name) });
        if (error) throw new Error(`${f.name}: ${error.message}`);
        setZustand((x) => ({ ...x, anlagen: [...x.anlagen, { name: f.name, mime: f.type || mimeAusName(f.name), storage: { bucket: 'email-anhaenge', path } }] }));
      }
    } catch (e) { setFehler((e as Error).message); } finally { setLaedtHoch(false); if (datei.current) datei.current.value = ''; }
  };

  const senden = async () => {
    if (!konto) return;
    setFehler(null); setErgebnis(null);
    setLaeuft({ fertig: 0, gesamt: versendbar.length });
    try {
      if (z.gesendetId) throw new Error('Dieses Schreiben wurde bereits gesendet.');
      // Etappe 2: Briefkopf/Absender auflösen und mit ins Protokoll (Fortsetzen nutzt dieselben)
      const kopf = standardBriefkopf(gestaltung.koepfe, z.briefkopfId);
      const absender = gestaltung.absender.find((a) => a.id === z.absenderId) ?? null;
      const pdfGestaltung = await ladePdfGestaltung(kopf, absender);
      // Sperrvermerke unmittelbar vor dem Senden frisch – Gesperrte werden im Protokoll als Fehler mit Grund vermerkt
      const frisch = mitSperrvermerken(versendbar.map(({ e }) => e), await ladeSperrvermerke(versendbar.map(({ e }) => e)));
      const lauf = await legeLaufAn(
        { vorlageId: z.vorlageId, betreff: z.betreff, text: z.text, eingaben: z.eingaben, anlagen: z.anlagen, optionen: z.optionen, begleittext: z.begleittext,
          briefkopfId: kopf.id === EINGEBAUT.id ? null : kopf.id, absenderId: absender?.id ?? null, schriftform: z.schriftform,
          wiedervorlage: z.wiedervorlage?.an && z.wiedervorlage.datum ? { datum: z.wiedervorlage.datum, notiz: z.wiedervorlage.notiz, vorgangsartId: z.vorgangsartId } : null },
        versendbar.map(({ e, wege }, i) => ({ e: { ...frisch[i], werte: werteVon(z, e) }, wege })),
      );
      setZustand((x) => ({ ...x, gesendetId: lauf.schreibenId }));
      const r = await fuehreAus(lauf, standardDienste(konto, lauf.schreibenId, pdfGestaltung, signatur?.html ?? null), (fertig, gesamt) => setLaeuft({ fertig, gesamt }));
      setErgebnis(r);
      // Wiedervorlage nur, wenn etwas rausging – ein Ticket je Schreiben (sonst legt „Fortsetzen“ es nach)
      if (lauf.wiedervorlage) {
        if (r.gesendet + r.teilweise > 0) {
          try { setTicket(await wiedervorlageNachVersand({ schreibenId: lauf.schreibenId, betreff: z.betreff, eingaben: z.eingaben, empfaenger: lauf.empfaenger.map((x) => x.e), wunsch: lauf.wiedervorlage })); }
          catch (e) { setTicketFehler(`Wiedervorlage nicht angelegt: ${(e as Error).message}`); }
        } else setTicketFehler('Wiedervorlage noch nicht angelegt, weil nichts rausging – „Fortsetzen“ im Postausgang legt sie nach.');
      }
    } catch (e) { setFehler((e as Error).message); } finally { setLaeuft(null); setBestaetigung(''); }
  };

  const karte: React.CSSProperties = { borderRadius: 22, padding: mobil ? 12 : 16, gap: 10 };
  const freigabe = !!konto && versendbar.length > 0 && bestaetigung.trim() === String(versendbar.length) && !laeuft && !ergebnis && !z.gesendetId && gestaltung.bereit && !wvOhneDatum;
  return (
    <section className="flex flex-col" style={{ gap: 12 }}>
      <div className="immo-glas flex flex-col" style={karte}>
        <span style={{ fontSize: 14, fontWeight: 800 }}>Anlagen</span>
        {z.anlagen.map((a, i) => (
          <span key={`${a.name}-${i}`} className="flex items-center" style={{ gap: 8, fontSize: 13 }}>
            <Paperclip size={14} /><span className="min-w-0 flex-1 truncate">{i + 1}. {a.name}</span>
            <button type="button" aria-label={`${a.name} entfernen`} onClick={() => setZustand((x) => ({ ...x, anlagen: x.anlagen.filter((_, j) => j !== i) }))}
              style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink2)', cursor: 'pointer' }}><X size={12} weight="bold" /></button>
          </span>
        ))}
        {z.anlagen.length > 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Unter dem Schreiben steht automatisch „Anlagen: 1. …“. Per E-Mail als Anhänge, per Post im selben Umschlag.</span>}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <input ref={datei} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple hidden onChange={(ev) => void hochladen(ev.target.files)} />
          <ImmoButton groesse={36} icon={laedtHoch ? CircleNotch : UploadSimple} onClick={() => datei.current?.click()} disabled={laedtHoch || !konto}>Datei hochladen</ImmoButton>
          <ImmoButton groesse={36} icon={Folder} onClick={() => setAkte((a) => !a)}>Aus der Akte wählen</ImmoButton>
        </div>
        {akte && <AktenAuswahl objektIds={objektIds} onWahl={(d) => { setZustand((x) => ({ ...x, anlagen: [...x.anlagen, { name: d.name, mime: d.mimeType, drive: d.id }] })); setAkte(false); }} />}
      </div>

      <div className="immo-glas flex flex-col" style={karte}>
        <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>Versandweg</span>
          <span className="immo-zahl" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{zaehler.email} per E-Mail · {zaehler.post} per Post · {zaehler.portal} ins Portal · {zaehler.druck} zum Drucken</span>
        </div>
        {druckDa && <SchalterZeile klein an={z.schriftform} onChange={setzeSchriftform} label="Schriftform (Original mit Unterschrift)"
          zusatz="Z. B. Kündigung: Original aus dem Druckstapel, von Hand unterschrieben. E-Mail und Portal gehen nur zusätzlich als Vorab-Info." />}
        {z.schriftform && mitPost && (
          <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '8px 12px', borderRadius: 12, background: 'var(--warn-soft)', color: 'var(--warn)' }}>
            Die Post (E-POST) druckt die Unterschrift nur als Bild. Für Kündigungen von Wohnraum braucht es eine eigenhändige Unterschrift – dafür „Druck“ wählen.
          </span>
        )}
        {mitPortal && (
          <div className="flex flex-col" style={{ gap: 6 }}>
            <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)' }}>Für alle:</span>
              {KOMBIS.map((k) => (
                <ImmoButton key={k.label} groesse={32} onClick={() => setzeFuerAlle(k.wege)} data-tip={k.tip}>{k.label}</ImmoButton>
              ))}
            </div>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
              Freigabe = im Mieter- bzw. Eigentümerportal. Jeder sieht nur sein eigenes Schreiben (auch beim Serienbrief); es wird dafür nichts zusätzlich verschickt.
              Allgemeine Unterlagen für alle eines Hauses gibt man im Aktenschrank frei.
            </span>
          </div>
        )}
        <ul className="immo-scroll m-0 flex flex-col overflow-y-auto p-0" style={{ gap: 4, listStyle: 'none', maxHeight: 380 }}>
          {liste.map(({ e, wege, m, unmoeglich, sperre }) => (
            <li key={schluessel(e)} className="flex flex-wrap items-center" style={{ gap: 8, padding: '6px 10px', borderRadius: 12, background: 'var(--fill)' }}>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{e.name}</span>
                <span className="truncate" style={{ fontSize: 12, color: sperre || unmoeglich.length ? 'var(--bad)' : 'var(--ink3)' }}>
                  {sperre ?? (unmoeglich.length ? `${unmoeglich.map((w) => ZUSTELLWEG_LABEL[w]).join(', ')} nicht möglich` : [e.email, e.zustellwege.length ? `bevorzugt: ${e.zustellwege.map((w) => ZUSTELLWEG_LABEL[w]).join(', ')}` : null,
                    wege.includes('portal') && portalOhneLogin(e) ? 'Portal: noch ohne Zugang – sichtbar ab Freischaltung' : null].filter(Boolean).join(' · '))}
                </span>
              </span>
              <span className="flex" role="group" aria-label={`Zustellwege ${e.name}`} style={{ gap: 4 }}>
                {angeboteneWege.map((w) => {
                  const an = wege.includes(w);
                  return (
                    <button key={w} type="button" aria-pressed={an} disabled={!m[w] && !an}
                      title={!m[w] ? (w === 'portal' ? 'Nur für Mieter und Eigentümer mit Objektakte' : w === 'post' ? 'Keine vollständige Anschrift' : 'Keine E-Mail-Adresse')
                        : w === 'druck' ? 'Druckstapel fürs Büro: ausdrucken, unterschreiben, selbst versenden' : z.schriftform && (w === 'email' || w === 'portal') ? 'Nur als Vorab-Info' : undefined}
                      onClick={() => setZustand((x) => {
                        const alt = x.wege[schluessel(e)] ?? [];
                        return { ...x, wege: { ...x.wege, [schluessel(e)]: an ? alt.filter((y) => y !== w) : ZUSTELLWEGE.filter((y) => y === w || alt.includes(y)) } };
                      })}
                      style={{ height: 32, padding: '0 10px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 800, cursor: !m[w] && !an ? 'not-allowed' : 'pointer',
                        background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink2)', opacity: !m[w] && !an ? 0.4 : 1 }}>
                      {ZUSTELLWEG_LABEL[w]}
                    </button>
                  );
                })}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {(mitPost || mitDruck) && (
        <div className="immo-glas flex flex-col" style={karte}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>{mitPost ? 'Post' : 'Einschreiben'}</span>
          {mitPost && <SchalterZeile klein an={!!z.optionen.duplex} onChange={(v) => setZustand((x) => ({ ...x, optionen: { ...x.optionen, duplex: v } }))} label="Beidseitig drucken" />}
          {mitPost && <SchalterZeile klein an={!!z.optionen.farbe} onChange={(v) => setZustand((x) => ({ ...x, optionen: { ...x.optionen, farbe: v } }))} label="Farbdruck" />}
          <select value={z.optionen.einschreiben ?? ''} aria-label="Einschreiben" onChange={(ev) => setZustand((x) => ({ ...x, optionen: { ...x.optionen, einschreiben: ev.target.value as BriefOptionen['einschreiben'] } }))}
            style={{ ...FELD, width: 'auto', minWidth: 220, height: 40 }}>
            <option value="">Normaler Brief</option>
            {EINSCHREIBEN.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          {mitDruck && z.optionen.einschreiben && (
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Druckstapel: das Einschreiben selbst bei der Post aufgeben und die Sendungsnummer im Druckstapel eintragen.</span>
          )}
        </div>
      )}

      {z.wiedervorlage && (
        <div className="immo-glas flex flex-col" style={karte}>
          <SchalterZeile klein an={z.wiedervorlage.an} onChange={(v) => setzeWv({ an: v })}
            label="Wiedervorlage anlegen" zusatz={`Nach dem Senden ein Ticket „Wiedervorlage“ für dieses Schreiben${z.vorgangsartId ? ' – als Vorgang der Vorlage' : ''}.`} />
          {z.wiedervorlage.an && (
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              <input type="date" aria-label="Wiedervorlage am" value={z.wiedervorlage.datum} min={heuteIso}
                onChange={(ev) => setzeWv({ datum: ev.target.value })}
                style={{ ...FELD, width: 170, height: 40 }} />
              <input aria-label="Notiz zur Wiedervorlage" value={z.wiedervorlage.notiz} placeholder="Notiz, z. B. Antwort des Mieters prüfen"
                onChange={(ev) => setzeWv({ notiz: ev.target.value })}
                style={{ ...FELD, flex: '1 1 220px', minWidth: 0, height: 40 }} />
            </div>
          )}
          {wvOhneDatum && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>Bitte ein Datum für die Wiedervorlage wählen oder sie ausschalten.</span>}
        </div>
      )}

      <div className="immo-glas flex flex-col" style={karte}>
        {versendbar.length >= 6 && (
          <span role="status" style={{ fontSize: 12.5, fontWeight: 700, padding: '8px 12px', borderRadius: 12, background: 'var(--warn-soft)', color: 'var(--warn)' }}>
            {versendbar.length} echte Empfänger – zum Testen bitte nur Testkonten im Objekt 0000 verwenden.
          </span>
        )}
        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={{ ...LABEL, fontWeight: 600, color: 'var(--ink2)' }}>
            Zur Bestätigung die Zahl <b className="immo-zahl" style={{ color: 'var(--ink)' }}>{versendbar.length}</b> eintragen
            {liste.length > versendbar.length ? ` (${liste.length - versendbar.length} werden nicht gesendet)` : ''}:
          </span>
          <input inputMode="numeric" value={bestaetigung} onChange={(ev) => setBestaetigung(ev.target.value)} placeholder={String(versendbar.length)} style={{ ...FELD, width: 160, fontWeight: 800 }} />
        </label>
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <ImmoButton art="akzent" groesse={48} icon={laeuft ? CircleNotch : PaperPlaneTilt} onClick={() => void senden()} disabled={!freigabe}>
            {laeuft ? `${laeuft.fertig} / ${laeuft.gesamt} …` : `${versendbar.length} ${versendbar.length === 1 ? 'Schreiben' : 'Schreiben'} senden`}
          </ImmoButton>
          {konto && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>E-Mails von {konto.email_adresse}{zaehler.email > 0 ? (signatur ? `, mit deiner Signatur „${signatur.name}“` : ' – ohne persönliche Signatur (anlegen unter E-Mails › Einstellungen)') : ''}</span>}
        </div>
        {z.gesendetId && !ergebnis && !laeuft && (
          <span role="status" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>
            Dieses Schreiben ist bereits gesendet. Stand und „Fortsetzen“ unter Dokumente › Postausgang › Schreiben – für ein weiteres bitte „Neues Schreiben“ beginnen.
          </span>
        )}
        {fehler && <span role="alert" className="flex items-start" style={{ gap: 6, fontSize: 13, color: 'var(--bad)' }}><WarningCircle size={14} weight="fill" style={{ marginTop: 2 }} />{fehler}</span>}
        {ergebnis && (
          <div role="status" className="flex flex-col" style={{ gap: 6 }}>
            <span className="flex items-center" style={{ gap: 6, fontSize: 13.5, fontWeight: 800, color: ergebnis.fehler || ergebnis.teilweise ? 'var(--warn)' : 'var(--ok)' }}>
              <CheckCircle size={16} weight="fill" />{ergebnis.gesendet} gesendet{ergebnis.teilweise ? ` · ${ergebnis.teilweise} nur teilweise` : ''}{ergebnis.fehler ? ` · ${ergebnis.fehler} Fehler` : ''}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>
              Protokoll und „Fortsetzen“ für Fehlgeschlagene unter Dokumente › Postausgang › Schreiben.{mitDruck ? ' Briefe zum Drucken liegen dort im Druckstapel.' : ''}
            </span>
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              <ImmoButton groesse={36} onClick={() => navigate('/admin/dokumente?tab=post')}>Zum Postausgang</ImmoButton>
              {ticket && <ImmoButton groesse={36} onClick={() => navigate(`/admin/tickets/${ticket.id}`)}>Wiedervorlage {ticket.ticket_nummer}</ImmoButton>}
            </div>
            {ticketFehler && <span role="alert" style={{ fontSize: 12.5, color: 'var(--bad)' }}>{ticketFehler}</span>}
          </div>
        )}
      </div>
    </section>
  );
}
